import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Plot from 'react-plotly.js';
import Swal from 'sweetalert2';
import Papa from 'papaparse';
import { FiInfo } from 'react-icons/fi';
import Style from '../assets/css/draw.module.css';
import Loading from './loading';

const Draw = () => {
    const [nombrePlaneta, setNombrePlaneta] = useState('');
    const [plotData, setPlotData] = useState(null);
    const [error, setError] = useState(null);
    const [planetNames, setPlanetNames] = useState([]);
    const [showDropdown, setShowDropdown] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const { t } = useTranslation();

    useEffect(() => {
        Papa.parse('/planetas.csv', {
            download: true,
            header: true,
            complete: (result) => {
                const names = result.data
                    .map((row) => row.pl_name)
                    .filter(name => typeof name === 'string' && name.trim() !== '');
                setPlanetNames(names);
            },
            error: (error) => {
                console.error("Error al leer el archivo CSV:", error);
            }
        });
    }, []);

    // Obtener el parámetro 'planeta' de la URL
    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const planetaParam = searchParams.get('planeta');

        if (planetaParam) {
            setNombrePlaneta(decodeURIComponent(planetaParam));
        }
    }, [location.search]);

    const fetchPlotData = async () => {
        setIsLoading(true); // Comienza la carga
        try {
            const apiUrl = `${import.meta.env.VITE_API_URL}/draw3D/api/mapa_estelar_3d?planeta=${nombrePlaneta}`;
            const response = await fetch(apiUrl);
            if (!response.ok) {
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: 'Error al obtener los datos del gráfico',
                });
            }
            const data = await response.json();
            const plot = JSON.parse(data);
            setPlotData(plot);
            setError(null);
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Error al obtener los datos del gráfico',
            });
            setPlotData(null);
        } finally {
            setIsLoading(false); // Termina la carga
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (nombrePlaneta.trim()) {
            fetchPlotData();
        } else {
            Swal.fire({
                icon: 'warning',
                text: t('pleaseEnterPlanetName'),
            });
        }
    };

    const handlePlanetSelect = (name) => {
        setNombrePlaneta(name);
        setShowDropdown(false);
    };

    return (
        <div className={Style.drawContainer}>
            <div className={Style.drawTitleContainer}>
                <h1 className={Style.drawTitle}>{t('drawTitle')}</h1>
            </div>
            <form onSubmit={handleSubmit} className={Style.drawForm}>
                <div className={Style.inputAndAlert}>
                    <input
                        type="text"
                        value={nombrePlaneta}
                        onChange={(e) => setNombrePlaneta(e.target.value)}
                        onClick={() => setShowDropdown(true)}
                        placeholder={t('planetName')}
                        className={Style.drawInput}
                    />
                    <FiInfo
                        className={Style.infoIcon}
                        onClick={() => Swal.fire({
                            icon: 'warning',
                            text: t('infoMessage')
                        })}
                    />
                </div>
                <div className={Style.buttonsGenerators}>
                    <Link
                        to={`/draw2D?planeta=${encodeURIComponent(nombrePlaneta)}`}
                        className={Style.linkButton}
                    >
                        {t('drawButton2D')}
                    </Link>
                    <button type="submit" className={Style.drawButton}>{t('drawButton')}</button>
                </div>
                {showDropdown && (
                    <ul className={Style.dropdown}>
                        {planetNames
                            .filter((name) => name.toLowerCase().includes(nombrePlaneta.toLowerCase()))
                            .map((name, index) => (
                                <li
                                    key={index}
                                    className={Style.dropdownItem}
                                    onClick={() => handlePlanetSelect(name)}
                                >
                                    {name}
                                </li>
                            ))}
                    </ul>
                )}
            </form>

            {error && <p className={Style.error}>{error}</p>}

            {isLoading ? (
                <Loading />
            ) : plotData ? (
                <div className={Style.plotContainer}>
                    <Plot
                        data={plotData.data}
                        layout={{
                            ...plotData.layout,
                            autosize: true,
                            margin: { l: 0, r: 0, t: 0, b: 0 },
                            showlegend: false,
                            paper_bgcolor: '#111',
                            plot_bgcolor: '#111',
                        }}
                        useResizeHandler={true}
                        style={{ width: "100%", height: "100%" }}
                    />
                </div>
            ) : (
                <p className={Style.drawLoading}>{t('drawP')}</p>
            )}
        </div>
    );
};

export default Draw;